<template>
    <q-page padding>
        <div class="q-display-1 q-mb-md text-secondary">
            <q-icon name="far fa-user-chart" class="q-mb-sm" /> {{ $t('users_on_campaign') }}
        </div>

        <q-table
            :data="rewards_level"
            :columns="columns"
            :pagination.sync="server_pagination"
            @request="request"
            :loading="loading"
            :filter="filter"
            row-key="name"
            :no-data-label="$t('no_data_avaliable')"
            :no-results-label="$t('no_results_found')"
            :loading-label="$t('loading_data')"
            :rows-per-page-label="$t('items_per_page')"
            :rows-per-page-options="[5,10,50,100]"
            binary-state-sort
            >

            <template slot="top" slot-scope="props" :props="props">
                <q-search v-model="filter" icon="far fa-search" :placeholder="$t('search')" :debounce="500" />
                <div class="col" />
                <q-btn color="positive" icon="fal fa-plus" :label="$t('add_user_to_campaign')" @click="show_add_user_to_campaign = true" />
            </template>

            <q-tr slot="header" slot-scope="props" :props="props">
                <q-th v-for="col in props.cols" :key="col.name" :props="props">
                {{ $t(col.label) }}
                </q-th>
            </q-tr>

            <q-tr slot="body" slot-scope="props" :props="props">
                <q-td auto-width key="id" :props="props">
                    {{ props.row.id }}
                </q-td>

                <q-td key="user_full_name" :props="props">
                    {{ props.row.user_full_name }}
                </q-td>

                <q-td key="user_email" :props="props">
                    {{ props.row.user_email }}
                </q-td>

                <q-td key="total_earned_points" :props="props">
                    {{ props.row.total_earned_points }}
                </q-td>

                <q-td key="user_current_points" :props="props">
                    {{ props.row.user_current_points }}
                </q-td>

                <q-td key="reward_level_title" :props="props">
                    {{ props.row.reward_level_title }}
                </q-td>

                <q-td key="campaign" :props="props">
                    {{ props.row.campaign }}
                </q-td>

                <q-td key="created_date" :props="props">
                    {{ fDate(props.row.created_date) }}
                </q-td>

                <q-td key="allowed_to_level_up" :props="props">
                    <q-chip small :color="colorChip(props.row.allowed_to_level_up)">
                        <i class="far fa-chevron-up"></i>
                    </q-chip>
                </q-td>

                <q-td key="actions" :props="props">
                    <q-btn flat round color="primary" icon="far fa-pencil-alt" @click="$router.push({ name: 'users_reward_level', params: { id: props.row.actions } })" />
                </q-td>
            </q-tr>
        </q-table>

        <q-modal v-model="show_add_user_to_campaign" :content-css="{minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '90vh'}" @hide="hideShowUserToCampaign()">
            <q-modal-layout>
                <q-toolbar slot="header">
                    <q-btn flat round dense @click="hideShowUserToCampaign()" icon="fas fa-times" />
                    <q-toolbar-title>
                        {{ $t('add_user_to_campaign') }}
                    </q-toolbar-title>
                </q-toolbar>

                <div class="layout-padding">
                    <div class="row gutter-sm">
                        <div class="col-12">
                            <q-input v-model="user" :float-label="$t('user')" :error="$v.user_id.$error" @input="$v.user_id.$touch" clearable @click="clearUserCampaign()">
                                <q-autocomplete
                                    @search="searchUser"
                                    :min-characters="3"
                                    @selected="selectUser"
                                    :value-field="v => `${user.full_name} - ${user.email}`"
                                    separator
                                />
                            </q-input>
                        </div>

                        <div class="col-12">
                            <q-select v-model="campaign" :options="campaigns" :float-label="$t('campaign')" :error="$v.campaign.$error" @input="$v.campaign.$touch" />
                        </div>

                        <div class="col-12 text-right">
                            <q-btn color="negative" icon="fal fa-ban" :label="$t('cancel')" class="q-mr-md" @click="hideShowUserToCampaign()" />
                            <q-btn color="positive" icon="fal fa-save" :label="$t('save')" @click="addUserToCampaign()" />
                        </div>

                        <div class="col-12 q-mt-md" v-if="errorList.length > 0">
                            <q-list dense separator>
                                <q-list-header>{{ $t('error') }}</q-list-header>
                                <q-item v-for="(error, index) in errorList" :key="index">
                                    <q-item-main :label="$t(error)" />
                                </q-item>
                            </q-list>
                        </div>
                    </div>
                </div>
            </q-modal-layout>
        </q-modal>
    </q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { required } from 'vuelidate/lib/validators'
import { date } from 'quasar'
const { formatDate } = date


export default {
    i18nOptions: { namespaces: [ "custom", "common", "tags", "admin", "rewards" ] },

    data() {
        return {
            server_pagination: {
                page: 1,
                rowsNumber: 10,
                rowsPerPage: null,
                descending: false,
                sortBy: null
            },
            loading: false,
            rewards_level: [],
            columns: [
                {
                    name: 'id',
                    required: true,
                    label: '#',
                    align: 'left',
                    field: 'id',
                    sortable: true,
                },
                {
                    name: 'user_full_name',
                    required: true,
                    label: 'name',
                    align: 'left',
                    field: 'user_full_name',
                    sortable: true,
                },
                {
                    name: 'user_email',
                    required: true,
                    label: 'email',
                    align: 'left',
                    field: 'user_email',
                    sortable: true,
                },
                {
                    name: 'total_earned_points',
                    required: true,
                    label: 'total_points',
                    align: 'left',
                    field: 'total_earned_points',
                    sortable: true,
                },
                {
                    name: 'user_current_points',
                    required: true,
                    label: 'points_for_use',
                    align: 'left',
                    field: 'user_current_points',
                    sortable: true,
                },
                {
                    name: 'reward_level_title',
                    required: true,
                    label: 'level',
                    align: 'left',
                    field: 'reward_level_title',
                    sortable: true,
                },
                {
                    name: 'campaign',
                    required: true,
                    label: 'campaign',
                    align: 'left',
                    field: 'campaign',
                    sortable: true,
                },
                {
                    name: 'created_date',
                    required: true,
                    label: 'inscription_date',
                    align: 'left',
                    field: 'created_date',
                    sortable: true,
                },
                {
                    name: 'allowed_to_level_up',
                    required: true,
                    label: 'level_up',
                    align: 'left',
                    field: 'allowed_to_level_up',
                    sortable: true,
                },
                {
                    name: 'actions',
                    required: true,
                    label: '',
                    align: 'right',
                    field: 'edit',
                    sortable: false,
                },
            ],
            filter: '',
            show_add_user_to_campaign: false,
            user: '',
            campaign: '',
            user_id: '',
            campaigns: [],
            errors: {}
        }
    },

    computed: {
        ...mapGetters({
            access_token: 'common/access_token',
            id_user_client_country_selected: 'common/id_user_client_country_selected',
            reload_content: 'common/reload_content',
            areas: 'registration_lists/country_areas',
            profiles: 'registration_lists/profiles',
            reward_types: 'registration_lists/reward_types',
            num_rows: 'datatable/num_rows',
            id_user_language_selected: 'common/id_user_language_selected',
        }),

        getHeader() {
            let headers = {}
            return headers = {
                headers: {
                    'content-type': 'application/json',
                    'authorization': 'Basic ' + btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
                    'authorization': 'Bearer ' + this.access_token
                }
            }
        },

        getMaxWidth() {
            if (this.$q.platform.is.mobile) return '100vw'
            else return '80vw'
        },

        getMinWidth() {
            if (!this.$q.platform.is.mobile) return '90vw'
        },

        errorList() {
            let errors = []
            Object.keys(this.errors).forEach(element => {
                if(element === 'profile') errors.push('user_does_not_have_the_profile_necessary_to_participate_in_the_campaign')
                else errors.push('')
            })
            return errors
        }
    },

    methods: {
        request ({ pagination }) {
            this.loading = true
            this.server_pagination = pagination

            let order = pagination.descending ? '-' : ''
            let order_by = pagination.sortBy ? order + pagination.sortBy : ''

            let data = {
                "page_size": this.server_pagination.rowsPerPage,
                "page": this.server_pagination.page,
                "search": this.filter,
                "ordering": order_by
            }

            this.savePagination()

            this.$axios.get('/admin_rewards/users_reward_levels/?' + this.$qs.stringify(data), this.getHeader)
            .then(response => {
                this.server_pagination.rowsNumber = response.data.count
                this.rewards_level = response.data.results.map( e => {
                    return {
                        ...e,
                        actions: e.id
                    }
                })
                this.loading = false
            })
            .catch(e => {
                this.loading = false
                console.log(e)
            })
        },

        fetchLists() {
            this.$q.loading.show()

            this.$axios.get('/accounts/get_registration_lists/', this.getHeader)
            .then(response => {
                this.$store.dispatch('registration_lists/SET_LISTS', [response.data])
                this.$q.loading.hide()

                this.request({
                    pagination: this.server_pagination
                })
            })
            .catch(e => {
                this.$q.loading.hide()
                console.log(e)
            })
        },

        getStatus(status) {
            if (status == 1) return 'active_f'
            else if (status == 2) return 'inactive_f'
            else if (status == 3) return 'deleted_f'
        },

        getIconStatus(status) {
            if (status == 1) return 'fas fa-check'
            else if (status == 2) return 'fas fa-ban'
            else if (status == 3) return 'fas fa-trash-alt'
        },

        getColorStatus(status) {
            if (status == 1) return 'positive'
            else if (status == 2) return 'warning'
            else if (status == 3) return 'negative'
        },

        savePagination() {
            this.$store.dispatch('datatable/SET_NUM_ROWS', [this.server_pagination.rowsPerPage])
        },

        loadPagination() {
            this.server_pagination.rowsPerPage = this.num_rows
        },

        fDate(d) {
            if (d) {
                let format = ''

                switch(this.id_user_language_selected) {
                    case 1:
                    case 3: format = 'DD/MM/YYYY HH:mm'
                    break
                    default: format = 'MM-DD-YYYY HH:mm'
                    break
                }

                return date.formatDate(d, format)
            }
        },

        colorChip(e) {
            return e ? "positive" : 'light'
        },

        getCampaigns() {
            this.$axios.get(`/admin_rewards/users/get_user_campaigns/${this.user_id}/`, this.getHeader)
            .then(response => {
                this.campaigns = response.data.map(e => ({...e, value: e.id, label: e.title}))
            })
            .catch(e => {
                console.log(e)
            })
        },

        searchUser(terms, done) {
            this.$axios.get(`/admin_rewards/users/get_users_without_campaign/?search=${terms}`, this.getHeader)
            .then(response => {
                let users = []
                users = response.data.map(e => ({ ...e, value: e.id, label: `${e.full_name} - ${e.email}` }))
                done(users)
            })
            .catch(e => {
                done([])
                console.log(e)
            })
        },

        selectUser(item, keyboard) {
            if (!keyboard) {
                this.user = `${item.full_name} - ${item.email}`
                this.user_id = item.id

                this.getCampaigns()
            }
        },

        addUserToCampaign() {
            this.$v.$touch()

            if (!this.$v.$error) {
                this.errors = {}
                this.$q.loading.show()

                let fd = new FormData()
                fd.append('user', this.user_id)
                fd.append('campaign', this.campaign)

                this.$axios.post(`/admin_rewards/users_reward_levels/add_user_in_campaing/`, fd, this.getHeader)
                .then(response => {
                    this.hideShowUserToCampaign()

                    this.request({
                        pagination: this.server_pagination
                    })

                    this.$q.notify({
                        message: this.$t('user_successfully_added_to_campaign'),
                        type: 'positive',
                        icon: 'fal fa-save'
                    })
                })
                .catch(e => {
                    this.$q.notify({
                        message: this.$t('error_adding_user_to_campaign'),
                        type: 'negative',
                        icon: 'fal fa-ban'
                    })

                    this.errors = e.response.data
                    console.log(e)
                })
                .finally(() => {
                    this.$q.loading.hide()
                })
            }
        },

        hideShowUserToCampaign() {
            this.show_add_user_to_campaign = false
            this.user = ''
            this.user_id = ''
            this.campaign = ''
            this.campaigns = []
        },

        clearUserCampaign() {
            this.user = ''
            this.user_id = ''
            this.campaign = ''
            this.campaigns = []
        }
    },

    watch: {
        reload_content: function() {
            this.loadPagination()
            this.fetchLists()
        }
    },

    created() {
      this.loadPagination()
      this.fetchLists()
    },

    validations() {
        return {
            user_id: { required },
            campaign: { required }
        }
    }
}
</script>

<style lang="stylus" scoped>

</style>
